import React, { useState, useEffect, useRef } from 'react';

const InformationForm = ({ seminar, onSave }) => {
    const [seminarName, setSeminarName] = useState(seminar.name || '');
    const [location, setLocation] = useState(seminar.location || '');
    const [startDate, setStartDate] = useState(seminar.startDate || '');
    const [endDate, setEndDate] = useState(seminar.endDate || '');
    const [isMultiDay, setIsMultiDay] = useState(seminar.isMultiDay || false);
    const [isLocked, setIsLocked] = useState(seminar.isLocked || false);
    const [logoPreview, setLogoPreview] = useState(seminar.logoUrl || '');
    const [logoError, setLogoError] = useState('');
    const [visibility, setVisibility] = useState(seminar.visibility || 'privé');
    const [password, setPassword] = useState(seminar.password || '');
    const [hasBlog, setHasBlog] = useState(seminar.hasBlog || false);
    const [blogName, setBlogName] = useState(seminar.blogName || '');
    const logoInputRef = useRef(null);
    const [suggestions, setSuggestions] = useState([]);
    const locationRef = useRef(null);
    const initialSeminarName = useRef(seminar.name || '');
    
    const isFormValid = seminarName.trim() !== '' && 
                        startDate.trim() !== '' && 
                        (!isMultiDay || (isMultiDay && endDate.trim() !== '' && endDate >= startDate)) &&
                        (visibility !== 'privé' || password.trim() !== '');

    useEffect(() => {
        if (blogName === `L'album de ${initialSeminarName.current}` || blogName === '') {
            setBlogName(`L'album de ${seminarName}`);
        }
        initialSeminarName.current = seminarName;
    }, [seminarName]);

    useEffect(() => {
        if (location.length < 3) { setSuggestions([]); return; }
        const fetchSuggestions = async () => {
            try {
                const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(location)}&limit=5`);
                const data = await response.json();
                setSuggestions(data.features || []);
            } catch (error) { console.error("Erreur lors de la récupération des adresses:", error); setSuggestions([]); }
        };
        const debounceTimeout = setTimeout(() => fetchSuggestions(), 300);
        return () => clearTimeout(debounceTimeout);
    }, [location]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (locationRef.current && !locationRef.current.contains(event.target)) setSuggestions([]);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleValidate = () => {
        const newLockedState = !isLocked;
        setIsLocked(newLockedState);
        onSave({ 
            ...seminar, 
            name: seminarName, 
            location, 
            startDate, 
            endDate: isMultiDay ? endDate : '', 
            isMultiDay, 
            logoUrl: logoPreview, 
            visibility, 
            password, 
            isLocked: newLockedState, 
            hasBlog, 
            blogName 
        });
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
        const maxSize = 80 * 1024;
        if (!allowedTypes.includes(file.type)) { setLogoError('Format invalide. PNG, JPG ou SVG uniquement.'); return; }
        if (file.size > maxSize) { setLogoError('Fichier trop volumineux (max 80 Ko).'); return; }
        setLogoError('');
        const reader = new FileReader();
        reader.onloadend = () => setLogoPreview(reader.result);
        reader.readAsDataURL(file);
    };

    const handleDeleteLogo = () => {
        setLogoPreview('');
        setLogoError('');
        if (logoInputRef.current) logoInputRef.current.value = null;
    };

    const handleBlogCheckboxChange = (e) => {
        const isChecked = e.target.checked;
        setHasBlog(isChecked);
        if (isChecked && blogName === '') {
            setBlogName(`L'album de ${seminarName}`);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-4 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
                <div className="space-y-8">
                    <div>
                        <label htmlFor="seminar-name" className="block text-sm font-medium text-gray-700 mb-1">Nom du séminaire*</label>
                        <input type="text" id="seminar-name" value={seminarName} disabled={isLocked} onChange={(e) => setSeminarName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F2507B] focus:border-[#F2507B] disabled:bg-gray-100" placeholder="Entrez le nom du séminaire ici" />
                    </div>
                    <div className="relative" ref={locationRef}>
                        <label htmlFor="seminar-location" className="block text-sm font-medium text-gray-700 mb-1">Lieu du séminaire</label>
                        <input type="text" id="seminar-location" value={location} disabled={isLocked} onChange={(e) => setLocation(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F2507B] focus:border-[#F2507B] disabled:bg-gray-100" placeholder="Entrez le lieu du séminaire ici" autoComplete="off" />
                        {suggestions.length > 0 && (
                            <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-b-lg mt-1 shadow-lg">
                                {suggestions.map(suggestion => (
                                    <div key={suggestion.properties.id} onClick={() => { setLocation(suggestion.properties.label); setSuggestions([]); }} className="p-2 hover:bg-gray-100 cursor-pointer">{suggestion.properties.label}</div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Visibilité</label>
                        <fieldset className="space-y-2">
                            <div className="flex items-center">
                                <input id="visibility-private" type="radio" name="visibility" value="privé" checked={visibility === 'privé'} onChange={(e) => setVisibility(e.target.value)} disabled={isLocked} className="focus:ring-[#F2507B] h-4 w-4 text-[#F2507B] border-gray-300" />
                                <div className="ml-3"><label htmlFor="visibility-private" className="text-sm font-medium text-gray-700">Privé</label><p className="text-xs text-gray-500">Un mot de passe sera nécessaire pour accéder à la page.</p></div>
                            </div>
                            <div className="flex items-center">
                                <input id="visibility-public" type="radio" name="visibility" value="public" checked={visibility === 'public'} onChange={(e) => setVisibility(e.target.value)} disabled={isLocked} className="focus:ring-[#F2507B] h-4 w-4 text-[#F2507B] border-gray-300" />
                                <div className="ml-3"><label htmlFor="visibility-public" className="text-sm font-medium text-gray-700">Public</label></div>
                            </div>
                        </fieldset>
                    </div>
                    {visibility === 'privé' && (
                        <div>
                            <label htmlFor="seminar-password" className="block text-sm font-medium text-gray-700 mb-1">Mot de passe*</label>
                            <input type="password" id="seminar-password" value={password} disabled={isLocked} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F2507B] focus:border-[#F2507B] disabled:bg-gray-100" placeholder="Définir un mot de passe" />
                        </div>
                    )}
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="seminar-start-date" className="block text-sm font-medium text-gray-700 mb-1">Date de début*</label>
                            <input type="date" id="seminar-start-date" value={startDate} disabled={isLocked} onChange={(e) => setStartDate(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F2507B] focus:border-[#F2507B] disabled:bg-gray-100" />
                        </div>
                        <div className="relative flex items-start">
                            <div className="flex items-center h-5"><input id="multi-day-checkbox" name="multi-day" type="checkbox" checked={isMultiDay} disabled={isLocked} onChange={(e) => setIsMultiDay(e.target.checked)} className="focus:ring-[#F2507B] h-4 w-4 text-[#F2507B] border-gray-300 rounded disabled:opacity-50" /></div>
                            <div className="ml-3 text-sm"><label htmlFor="multi-day-checkbox" className={`font-medium text-gray-700 ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>Ajouter une date de fin</label></div>
                        </div>
                        {isMultiDay && (
                            <div>
                                <label htmlFor="seminar-end-date" className="block text-sm font-medium text-gray-700 mb-1">Date de fin*</label>
                                <input type="date" id="seminar-end-date" value={endDate} min={startDate} disabled={isLocked} onChange={(e) => setEndDate(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F2507B] focus:border-[#F2507B] disabled:bg-gray-100" />
                            </div>
                        )}
                    </div>
                     <div className="pt-4">
                       <button onClick={handleValidate} disabled={!isFormValid && !isLocked} className={`w-full text-white font-bold py-3 px-4 rounded-lg transition-colors text-lg ${isLocked ? 'bg-gray-500 hover:bg-gray-600' : isFormValid ? 'bg-[#F2507B] hover:bg-opacity-90' : 'bg-gray-400 cursor-not-allowed'}`}>{isLocked ? 'Modifier' : 'Valider'}</button>
                     </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Logo du séminaire</label>
                    <div className={`relative mt-1 w-full h-72 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center ${isLocked ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-[#22D6B5]'}`}>
                        <input ref={logoInputRef} type="file" id="logo-upload" className="sr-only" accept="image/png, image/jpeg, image/svg+xml" onChange={handleLogoChange} disabled={isLocked}/>
                        {logoPreview ? (
                            <>
                                <img src={logoPreview} alt="Aperçu du logo" className="w-full h-full object-contain rounded-lg p-2" />
                                {!isLocked && (<button onClick={handleDeleteLogo} className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1.5 hover:bg-red-700 focus:outline-none"><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg></button>)}
                            </>
                        ) : (
                            <label htmlFor="logo-upload" className={`text-center ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true"><path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                <span className="mt-2 block text-sm font-medium text-gray-600">Cliquez pour ajouter un logo</span>
                                <span className="block text-xs text-gray-500">PNG, JPG, SVG jusqu'à 80Ko</span>
                            </label>
                        )}
                    </div>
                    {logoError && <p className="text-sm text-red-600 mt-2">{logoError}</p>}
                    
                    <div className="mt-8 pt-8 border-t border-gray-200">
                        <div className="relative flex items-start">
                            <div className="flex items-center h-5">
                                <input
                                    id="has-blog-checkbox"
                                    name="has-blog"
                                    type="checkbox"
                                    checked={hasBlog}
                                    disabled={isLocked}
                                    onChange={handleBlogCheckboxChange}
                                    className="focus:ring-[#F2507B] h-4 w-4 text-[#F2507B] border-gray-300 rounded disabled:opacity-50"
                                />
                            </div>
                            <div className="ml-3 text-sm">
                                <label htmlFor="has-blog-checkbox" className={`font-medium text-gray-700 ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                    Ajouter un blog photo collaboratif
                                </label>
                            </div>
                        </div>

                        {hasBlog && (
                            <div className="mt-4">
                                <label htmlFor="blog-name" className="block text-sm font-medium text-gray-700 mb-1">Nom du blog</label>
                                <input
                                    type="text"
                                    id="blog-name"
                                    value={blogName}
                                    disabled={isLocked}
                                    onChange={(e) => setBlogName(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F2507B] focus:border-[#F2507B] disabled:bg-gray-100"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InformationForm;